<script lang="ts">
import type { CheckboxRootProps } from 'reka-ui'
import type { Component } from 'vue'
import type { Color } from '../styles/colors'

export interface CheckboxProps extends Pick<CheckboxRootProps, 'disabled' | 'required' | 'name' | 'value' | 'id' | 'defaultValue'> {
  as?: string | Component
  label?: string
  description?: string
  color?: Color
  variant?: 'list' | 'card'
  indicatorPosition?: 'start' | 'end' | 'hidden'
  isChecked?: boolean
  class?: string | object
  withoutBorder?: boolean
}

export interface CheckboxEmits {
  change: [payload: Event]
}

export interface CheckboxSlots {
  label?: (props: { label?: string }) => any
  description?: (props: { description?: string }) => any
  /** Content aligned to the right of the line (after the label / the description) */
  trailing?: () => any
}

const ui = {
  root: 'relative flex',
  hover: 'hover:bg-{color}-50',
  container: 'flex items-center',
  base: 'size-4 rounded border overflow-hidden transition-colors',
  indicator: {
    base: 'flex items-center justify-center text-inverted',
    start: {
      root: 'flex-row',
      base: '',
      wrapper: 'ms-2',
    },
    end: {
      root: 'flex-row-reverse justify-between',
      base: '',
      wrapper: 'me-2',
    },
    hidden: {
      root: '',
      base: 'sr-only',
      wrapper: 'w-full text-center',
    },
  },
  icon: 'shrink-0 size-2.5 text-white',
  wrapper: '',
  trailing: 'shrink-0 flex items-center',
  label: 'block font-medium text-md cursor-pointer',
  description: 'text-muted text-sm text-secondary-800 mt-0.5',
  variant: {
    card: {
      root: 'bg-white px-3.5 py-3 border border-muted rounded-lg items-center',
    },
    list: {
      root: 'items-start',
    },
  },
  withoutBorder: 'border-transparent!',
  required: {
    label: 'after:content-[\'*\'] after:ms-0.5 after:text-red-600',
  },
  disabled: {
    root: 'opacity-30',
    label: 'cursor-not-allowed',
  },
  color: {
    base: 'bg-white not-disabled:hover:bg-secondary-100 border-secondary-600',
    variant: {
      card: {
        root: 'border-secondary-400',
      },
    },
  },
  checked: {
    color: {
      base: 'bg-{color}-500 not-disabled:hover:bg-{color}-600 not-disabled:hover:border-{color}-600 border-{color}-500 not-disabled:hover:border-{color}-600',
      secondary: 'bg-secondary-900 not-disabled:hover:bg-black not-disabled:hover:border-black border-secondary-900 not-disabled:hover:border-black',
    },
    variant: {
      card: {
        root: 'border-{color}-500 ring-1 ring-{color}-200',
      },
    },
  },
}
</script>

<script setup lang="ts">
import { reactivePick } from '@vueuse/core'
import { CheckboxIndicator, CheckboxRoot, Label, Primitive, useForwardProps } from 'reka-ui'
import { computed, useId, watch } from 'vue'
import icons from '../styles/icons'
import AwIcon from './AwIcon.vue'

defineOptions({ inheritAttrs: false })

const props = defineProps<CheckboxProps>()
const emits = defineEmits<CheckboxEmits>()
const slots = defineSlots<CheckboxSlots>()

const modelValue = defineModel<boolean | 'indeterminate'>({ default: undefined })

const hasModelValue = modelValue.value !== undefined

const rootProps = useForwardProps(reactivePick(props, 'required', 'value', 'defaultValue'))

const id = props.id ?? useId()

function onUpdate(value: any) {
  // @ts-expect-error - 'target' does not exist in type 'EventInit'
  const event = new Event('change', { target: { value } })
  emits('change', event)
}

const color = computed(() => props.color || 'primary')
const indicatorPosition = computed(() => props.indicatorPosition || 'start')
const variant = computed(() => props.variant || 'list')
const hasTrailingSlot = computed(() => !!slots.trailing)
const hasTextContent = computed(() =>
  !!(props.label || slots.label || props.description || slots.description),
)
const rootClass = computed(() => [
  ui.root,
  props.disabled && ui.disabled.root,
  props.class,
  hasTrailingSlot.value ? 'w-full flex-row items-center gap-2' : ui.indicator[indicatorPosition.value].root,
  ui.variant[variant.value].root,
  variant.value === 'card' && !modelValue.value && ui.hover.replaceAll('{color}', color.value),
  variant.value === 'card' && !modelValue.value && ui.color.variant.card.root,
  variant.value === 'card' && !!modelValue.value && ui.checked.variant.card.root.replaceAll('{color}', color.value),
  variant.value === 'card' && (props.disabled ? 'cursor-default' : 'cursor-pointer'),
  (props.withoutBorder && !modelValue.value) && ui.withoutBorder,
])
const checkboxContainerClass = computed(() => [
  ui.container,
  hasTrailingSlot.value && indicatorPosition.value === 'end' && 'order-2 shrink-0',
])
const baseClass = computed(() => [
  ui.base,
  ui.indicator[indicatorPosition.value].base,
  !!modelValue.value && (color.value === 'secondary' ? ui.checked.color.secondary : ui.checked.color.base).replaceAll('{color}', color.value),
  !modelValue.value && ui.color.base.replaceAll('{color}', color.value),
])
const indicatorClass = computed(() => [ui.indicator.base])
const iconClass = computed(() => ui.icon)
const labelClass = computed(() => [
  ui.label,
  props.required && ui.required.label,
  props.disabled && ui.disabled.label,
])
const wrapperClass = computed(() => [
  ui.wrapper,
  ...(hasTrailingSlot.value ? [] : [ui.indicator[indicatorPosition.value].wrapper]),
  hasTrailingSlot.value && hasTextContent.value && 'flex-1 min-w-0',
  hasTrailingSlot.value && hasTextContent.value && indicatorPosition.value === 'end' && 'order-1',
])
const trailingSlotClass = computed(() => [
  ui.trailing,
  hasTrailingSlot.value && indicatorPosition.value === 'end',
  hasTrailingSlot.value && !hasTextContent.value && 'ms-auto',
])

watch(() => props.isChecked, (value) => {
  if (!hasModelValue) {
    modelValue.value = !!value
  }
}, { immediate: true })
</script>

<template>
  <Primitive :as="(!variant || variant === 'list') ? as : Label" :class="rootClass">
    <div :class="checkboxContainerClass">
      <CheckboxRoot
        :id
        v-bind="{ ...rootProps, ...$attrs }"
        v-model="modelValue"
        :name :disabled :class="[baseClass, withoutBorder && 'border-none! hover:transition-none!', !modelValue && 'hover:bg-transparent!']"
        @update:model-value="onUpdate"
      >
        <!-- eslint-disable-next-line vue/no-template-shadow -->
        <template #default="{ modelValue }">
          <CheckboxIndicator :class="indicatorClass">
            <AwIcon v-if="modelValue === 'indeterminate'" :name="icons.minus" :class="iconClass" />
            <AwIcon v-else :name="icons.check" :class="iconClass" />
          </CheckboxIndicator>
        </template>
      </CheckboxRoot>
    </div>

    <div v-if="(label || !!slots.label) || (description || !!slots.description)" :class="wrapperClass">
      <component :is="(!variant || variant === 'list') ? Label : 'p'" v-if="label || !!slots.label" :for="id" :class="labelClass">
        <slot name="label" :label>
          {{ label }}
        </slot>
      </component>
      <p v-if="description || !!slots.description" :class="ui.description">
        <slot name="description" :description>
          {{ description }}
        </slot>
      </p>
    </div>

    <div v-if="slots.trailing" :class="trailingSlotClass">
      <slot name="trailing" />
    </div>
  </Primitive>
</template>
